import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './UserSettingScreen.css';
import { useAuth } from '../../context/authContext';
import { useLoading } from '../../context/loadingContext';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../../context/notificationContext';
import { isSuccessCode } from '../../helpers/apiResponse';
import { members } from '../../api/user';
import * as DelegateAPI from '../../api/delegate';
import { FormComponent } from '../../component/form/FormComponent';
import { Field } from '../../types/FromComponent.types';
import TableComponent from '../../component/form/TableComponent';
import { Column, TableButtons } from '../../types/TableComponent.types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { LoadingScreenLocal } from '../other/LoadingScreen';

type Member = {
    id: number;
    name: string;
    surname: string;
    role: string;
};

type Delegate = {
    id: number;
    athlete_id: number;
    parent_id: number;
    athlete_name: string;
    parent_name: string;
};

const DEFAULT_PAGE_OFFSET = 0;
const ROLE_KEYS = ['role', 'association_role', 'member_role'];

const getRole = (member: Record<string, unknown>): string => {
    const role = ROLE_KEYS.map((key) => member[key]).find((value) => value !== undefined);
    if (Array.isArray(role)) {
        return String(role[0] ?? '');
    }
    return String(role ?? '');
};

const resolveMemberName = (memberId: number, fallbackName: string, membersById: Record<number, Member>): string => {
    if (fallbackName && fallbackName.trim()) return fallbackName;
    const member = membersById[memberId];
    if (!member) return 'Unknown member';
    return `${member.name} ${member.surname}`.trim();
};

type DelegateFormData = {
    athlete_id?: string;
    parent_id?: string;
};

const AssociationSettingsScreen: React.FC = () => {
    const { association, associationRole } = useAuth();
    const { loading, setLoadingStatus } = useLoading();
    const { addNotification } = useNotification();
    const nav = useNavigate();

    const [memberList, setMemberList] = useState<Member[]>([]);
    const [delegateList, setDelegateList] = useState<Delegate[]>([]);

    const associationId = String(association.id);
    const canManageDelegation = associationRole === 'president' || associationRole === 'coach';

    const handleApiError = useCallback((requestId: string, errorCode: string) => {
        nav(`/500?process_id=${requestId}&error_code=${errorCode}`);
    }, [nav]);

    const loadMembersAndDelegates = useCallback(async () => {
        const membersResponse = await members(association.id, DEFAULT_PAGE_OFFSET, 20);
        if (!isSuccessCode(membersResponse.error_code)) {
            return handleApiError(membersResponse.request_id, membersResponse.error_code);
        }

        const mappedMembers = (Array.isArray(membersResponse.data) ? membersResponse.data : []).map((item: Record<string, unknown>) => ({
            id: Number(item.id ?? 0),
            name: String(item.name ?? ''),
            surname: String(item.surname ?? ''),
            role: getRole(item).toLowerCase(),
        })).filter((item) => item.id > 0);
        setMemberList(mappedMembers);

        if (!canManageDelegation) {
            return;
        }

        /*const delegateResponse = await DelegateAPI.list(associationId);
        if (!isSuccessCode(delegateResponse.error_code)) {
            return handleApiError(delegateResponse.request_id, delegateResponse.error_code);
        }

        const mappedDelegates = (Array.isArray(delegateResponse.data) ? delegateResponse.data : []).map((item: Record<string, unknown>) => {
            const athlete = (item.athlete ?? {}) as Record<string, unknown>;
            const parent = (item.parent ?? {}) as Record<string, unknown>;
            return {
                id: Number(item.id ?? item.delegate_id ?? 0),
                athlete_id: Number(item.athlete_id ?? athlete.id ?? 0),
                parent_id: Number(item.parent_id ?? parent.id ?? 0),
                athlete_name: String(item.athlete_name ?? athlete.name ?? ''),
                parent_name: String(item.parent_name ?? parent.name ?? ''),
            };
        }).filter((item) => item.id > 0);
        setDelegateList(mappedDelegates);*/
    }, [association.id, associationId, canManageDelegation, handleApiError]);

    useEffect(() => {
        if (!canManageDelegation) {
            nav('/403');
            return;
        }
        setLoadingStatus(true);
        loadMembersAndDelegates().finally(() => setLoadingStatus(false));
    }, [canManageDelegation, loadMembersAndDelegates, nav, setLoadingStatus]);

    const athletes = useMemo(() => memberList.filter((member) => member.role === 'athlete'), [memberList]);
    const parents = useMemo(() => memberList.filter((member) => member.role === 'parent'), [memberList]);
    const memberById = useMemo(() => {
        const mapped: Record<number, Member> = {};
        memberList.forEach((member) => { mapped[member.id] = member; });
        return mapped;
    }, [memberList]);

    const delegateFormFields: Field[] = [
        {
            name: 'athlete_id',
            placeholder: 'Athlete',
            type: 'dropdown',
            options: athletes.map((athlete) => ({ id: athlete.id, value: `${athlete.name} ${athlete.surname}`.trim() })),
        },
        {
            name: 'parent_id',
            placeholder: 'Parent',
            type: 'dropdown',
            options: parents.map((parent) => ({ id: parent.id, value: `${parent.name} ${parent.surname}`.trim() })),
        },
    ];

    const onAddDelegate = async (data: DelegateFormData) => {
        const athleteId = Number(data.athlete_id ?? 0);
        const parentId = Number(data.parent_id ?? 0);
        const validAthleteId = Number.isInteger(athleteId) && athleteId > 0;
        const validParentId = Number.isInteger(parentId) && parentId > 0;
        if (!validAthleteId || !validParentId) {
            return addNotification('Validation error', 'Please select both an athlete and a parent to create the delegation', 'error');
        }

        setLoadingStatus(true);
        const response = await DelegateAPI.add({ athlete_id: athleteId, parent_id: parentId }, associationId);
        if (!isSuccessCode(response.error_code)) {
            setLoadingStatus(false);
            return handleApiError(response.request_id, response.error_code);
        }
        addNotification('Delegation created', 'Athlete access delegated successfully', 'success');
        await loadMembersAndDelegates();
        setLoadingStatus(false);
    };

    const onRevokeDelegate = async (delegateId: number) => {
        setLoadingStatus(true);
        const response = await DelegateAPI.revoke(delegateId, associationId);
        if (!isSuccessCode(response.error_code)) {
            setLoadingStatus(false);
            return handleApiError(response.request_id, response.error_code);
        }
        addNotification('Delegation revoked', 'Delegation removed successfully', 'success');
        await loadMembersAndDelegates();
        setLoadingStatus(false);
    };

    const delegateRows = delegateList.map((item) => ({
        id: item.id,
        athlete: resolveMemberName(item.athlete_id, item.athlete_name, memberById),
        parent: resolveMemberName(item.parent_id, item.parent_name, memberById),
    }));
    const delegateColumns: Column[] = [
        { header: 'Athlete', accessor: 'athlete' },
        { header: 'Parent', accessor: 'parent' },
    ];
    const delegateButtons: TableButtons[] = [
        {
            button: <FontAwesomeIcon icon={faTrash} title="Revoke" />,
            bc: 'red',
            placeholder: 'Revoke',
            action: { param: 'id', function: onRevokeDelegate }
        }
    ];

    if (loading) return <LoadingScreenLocal />;

    return (
        <div className="settings-screen">
            <div className="settings-card">
                <h2>Association settings</h2>
                {/*<FormComponent
                    title="Delegate athlete access"
                    fields={delegateFormFields}
                    variant="left"
                    onSubmit={onAddDelegate}
                    button={{ name: 'Delegate access' }}
                />*/}
                <TableComponent data={delegateRows} columns={delegateColumns} buttons={delegateButtons} />
            </div>
        </div>
    );
};

export default AssociationSettingsScreen;
